	<template>
		<div class="about-page">
			<!-- Hero Banner -->
			<div class="hero-banner">
				<div class="hero-container">
					<div class="hero-content">
						<h1>{{ $t('aboutFst.heroTitle') }}</h1>
					</div>
				</div>
			</div>

			<!-- Main Content -->
			<main class="main-content">
				<!-- Company Introduction -->
				<section class="company-intro-section">
					<div class="container">
						<div class="section-header">
							<h2 class="section-title">
								<el-icon>
									<OfficeBuilding />
								</el-icon>
								{{ $t('aboutFst.companyIntro.title') }}
							</h2>
							<p class="section-subtitle">{{ $t('aboutFst.companyIntro.subtitle') }}</p>
						</div>

						<div class="intro-grid">
							<div class="intro-content">
								<div class="intro-card">
									<div class="card-header">
										<el-icon>
											<Medal />
										</el-icon>
										<h3>{{ $t('aboutFst.companyIntro.qualifications.title') }}</h3>
									</div>
									<p class="card-text">
										{{ $t('aboutFst.companyIntro.qualifications.part1') }}
										<span class="highlight">{{ $t('aboutFst.companyIntro.qualifications.highlight') }}</span>
										{{ $t('aboutFst.companyIntro.qualifications.part2') }}
									</p>
								</div>

								<div class="intro-card">
									<div class="card-header">
										<el-icon>
											<SetUp />
										</el-icon>
										<h3>{{ $t('aboutFst.companyIntro.coreBusiness.title') }}</h3>
									</div>
									<div class="service-tags">
										<div v-for="(item, index) in $t('aboutFst.companyIntro.coreBusiness.services')" :key="index"
											class="service-tag">
											{{ item }}
										</div>
									</div>
								</div>

								<div class="intro-card">
									<div class="card-header">
										<el-icon>
											<Trophy />
										</el-icon>
										<h3>{{ $t('aboutFst.companyIntro.advantages.title') }}</h3>
									</div>
									<div class="advantage-grid">
										<div class="advantage-item" v-for="(item, index) in $t('aboutFst.companyIntro.advantages.items')"
											:key="index">
											<el-icon>
												<component :is="getAdvantageIcon(index)" />
											</el-icon>
											<div>
												<div class="advantage-title">{{ item.title }}</div>
												<p>{{ item.content }}</p>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div class="intro-visual">
								<div class="company-image">
									<!-- 视频播放器（添加点击暂停功能） -->
									<video ref="videoPlayer" autoplay loop :muted="isMuted" playsinline class="intro-img"
										style="width: 100%; height: 100%; object-fit: cover" @click="togglePlay">
										<source :src="require('@/assets/video/fstvideo2.mp4')" type="video/mp4">
										<!-- 加载/错误状态 -->
										<div class="image-loading">
											<i class="el-icon-loading"></i>
										</div>
										<div class="image-error">
											<i class="el-icon-picture-outline"></i>
										</div>
									</video>

									<!-- 中央播放按钮（视频暂停时显示） -->
									<div class="center-play-button" v-show="!isPlaying" @click.stop="togglePlay">
										<i class="el-icon-video-play" />
									</div>

									<!-- 静音按钮（右下角） -->
									<div class="mute-button" @click.stop="toggleMute" style="cursor: pointer;">
										<!-- 音量开启（带声浪小喇叭） -->
										<svg v-if="!isMuted" class="icon" viewBox="0 0 1024 1024" width="20" height="20">
											<path
												d="M160 384c-17.7 0-32 14.3-32 32v192c0 17.7 14.3 32 32 32h160l192 192V192L320 384H160z m480-144c-8.8 0-16 7.2-16 16v512c0 8.8 7.2 16 16 16s16-7.2 16-16V256c0-8.8-7.2-16-16-16z m128 96c-8.8 0-16 7.2-16 16v320c0 8.8 7.2 16 16 16s16-7.2 16-16V352c0-8.8-7.2-16-16-16z m128 96c-8.8 0-16 7.2-16 16v128c0 8.8 7.2 16 16 16s16-7.2 16-16V448c0-8.8-7.2-16-16-16z"
												fill="currentColor" />
										</svg>

										<!-- 静音（只保留喇叭+叉） -->
										<svg v-else class="icon" viewBox="0 0 1024 1024" width="20" height="20">
											<path
												d="M160 384c-17.7 0-32 14.3-32 32v192c0 17.7 14.3 32 32 32h160l192 192V192L320 384H160zM704 512l96-96 48 48-96 96 96 96-48 48-96-96-96 96-48-48 96-96-96-96 48-48 96 96z"
												fill="currentColor" />
										</svg>
									</div>

									<!-- <div class="image-caption">{{ $t('aboutFst.companyImageCaption') }}</div> -->
								</div>
								<div class="stats-container">
									<div class="stats-grid">
										<div class="stat-item">
											<div class="stat-icon">
												<el-icon>
													<Aim />
												</el-icon>
											</div>
											<div class="stat-number">20+</div>
											<div class="stat-label">{{ $t('aboutFst.stats.airlines') }}</div>
										</div>
										<div class="stat-item">
											<div class="stat-icon">
												<el-icon>
													<User />
												</el-icon>
											</div>
											<div class="stat-number">3000+</div>
											<div class="stat-label">{{ $t('aboutFst.stats.clients') }}</div>
										</div>
										<div class="stat-item">
											<div class="stat-icon">
												<el-icon>
													<Team />
												</el-icon>
											</div>
											<div class="stat-number">200+</div>
											<div class="stat-label">{{ $t('aboutFst.stats.employees') }}</div>
										</div>
										<div class="stat-item">
											<div class="stat-icon">
												<el-icon>
													<OfficeBuilding />
												</el-icon>
											</div>
											<div class="stat-number">4000㎡</div>
											<div class="stat-label">{{ $t('aboutFst.stats.warehouse') }}</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="mission-statement">
							<el-icon>
								<Star />
							</el-icon>
							<div class="mission-content">
								<h3>{{ $t('aboutFst.companyIntro.mission.title') }}</h3>
								<p class="mission-paragraph">
									{{ $t('aboutFst.companyIntro.mission.prefix') }}
									<span class="highlight">{{ $t('aboutFst.companyIntro.mission.keywords1[0]') }}</span>
									{{ $t('aboutFst.companyIntro.mission.keywords2') }}
									<span class="highlight">{{ $t('aboutFst.companyIntro.mission.keywords[0]') }}</span>
									{{ $t('aboutFst.companyIntro.mission.middle1') }}
									<span class="highlight">{{ $t('aboutFst.companyIntro.mission.keywords[1]') }}</span>
									{{ $t('aboutFst.companyIntro.mission.middle2') }}
									<span class="highlight">{{ $t('aboutFst.companyIntro.mission.keywords[2]') }}</span>
									{{ $t('aboutFst.companyIntro.mission.suffix') }}
								</p>
							</div>
						</div>
					</div>
				</section>

				<!-- Development Timeline -->
				<section class="development-section">
					<div class="container">
						<div class="section-header center">
							<h2 class="section-title">{{ $t('aboutFst.timeline.title') }}</h2>
							<p class="section-subtitle">{{ $t('aboutFst.timeline.subtitle') }}</p>
						</div>
						<div class="timeline-container">
							<div class="timeline-track">
								<div class="timeline-item" style="left: 5%; top: 51%;" data-year="2005">
									<div class="timeline-events" style="margin-bottom: 16%;">
										<div class="event-item">{{ $t('aboutFst.timeline.events[0].content') }}</div>
									</div>
									<div class="timeline-dot"></div>
									<div class="timeline-year">2005</div>
								</div>

								<div class="timeline-item even" style="left:20%; top: 57%;" data-year="2016">
									<div class="timeline-year">2016</div>
									<div class="timeline-dot"></div>
									<div class="timeline-events" style="margin-top: 16%;">
										<div class="event-item">{{ $t('aboutFst.timeline.events[1].content') }}</div>
									</div>
								</div>

								<div class="timeline-item" style="left: 35%; top: 47%;" data-year="2018">
									<div class="timeline-events" style="margin-bottom: 16%;">
										<div class="event-item">{{ $t('aboutFst.timeline.events[2].content') }}</div>
									</div>
									<div class="timeline-dot"></div>
									<div class="timeline-year">2018</div>
								</div>

								<div class="timeline-item even" style="left: 47%; top: 75%;" data-year="2019">
									<div class="timeline-year">2019</div>
									<div class="timeline-dot"></div>
									<div class="timeline-events" style="margin-top: 16%;">
										<div class="event-item">{{ $t('aboutFst.timeline.events[3].content') }}</div>
									</div>
								</div>

								<div class="timeline-item" style="left: 60%; top: 53%;" data-year="2022">
									<div class="timeline-events" style="margin-bottom: 16%;">
										<div class="event-item">{{ $t('aboutFst.timeline.events[4].content') }}</div>
									</div>
									<div class="timeline-dot"></div>
									<div class="timeline-year">2022</div>
								</div>

								<div class="timeline-item even" style="left: 75%; top: 63%;" data-year="2023">
									<div class="timeline-year">2023</div>
									<div class="timeline-dot"></div>
									<div class="timeline-events" style="margin-top: 16%;">
										<div class="event-item">{{ $t('aboutFst.timeline.events[5].content') }}</div>
									</div>
								</div>

								<div class="timeline-item" style="left: 92%; top: 48%;" data-year="2025">
									<div class="timeline-events" style="margin-bottom: 16%;">
										<div class="event-item">{{ $t('aboutFst.timeline.events[6].content') }}</div>
									</div>
									<div class="timeline-dot"></div>
									<div class="timeline-year">2025</div>
								</div>
							</div>
						</div>
					</div>
				</section>

				<!-- Branches Section -->
				<section class="section branches-section">
					<div class="container">
						<div class="section-header center">
							<h2 class="section-title">
								<span class="title-decorator"></span>
								{{ $t('aboutFst.branches.title') }}
							</h2>
							<p class="section-subtitle">{{ $t('aboutFst.branches.subtitle') }}</p>
						</div>

						<div class="branch-selector">
							<div class="branch-tab" v-for="(branch, index) in branches" :key="index"
								:class="{ active: activeBranch === branch.city }" @click="activeBranch = branch.city">
								<div class="tab-content">
									<div class="city">{{ branch.city }}</div>
									<div class="year">{{ branch.year }}{{ $t('aboutFst.common.established') }}</div>
								</div>
								<div class="tab-indicator"></div>
							</div>
						</div>

						<transition name="fade-slide" mode="out-in">
							<div class="branch-detail" :key="activeBranch">
								<div class="branch-info">
									<h3>{{ currentBranch.city }}</h3>
									<div class="branch-desc">
										<p>{{ currentBranch.desc }}</p>
									</div>
									<div class="branch-stats">
										<div class="stat-item">
											<div class="stat-icon">
												<i class="el-icon-office-building"></i>
											</div>
											<div class="stat-text">
												<div class="stat-title">{{ $t('aboutFst.common.officeArea') }}</div>
												<div class="stat-value">{{ currentBranch.officeArea }}</div>
											</div>
										</div>
										<div class="stat-item">
											<div class="stat-icon">
												<i class="el-icon-house"></i>
											</div>
											<div class="stat-text">
												<div class="stat-title">{{ $t('aboutFst.common.warehouseArea') }}</div>
												<div class="stat-value">{{ currentBranch.warehouseArea }}</div>
											</div>
										</div>
									</div>
								</div>

								<div class="branch-gallery">
									<el-carousel :interval="3000" indicator-position="outside" height="90%" arrow="always" trigger="click"
										:autoplay="true">
										<el-carousel-item v-for="(img, imgIndex) in currentBranch.images" :key="imgIndex">
											<el-image :src="img" fit="cover" class="branch-image" :preview-src-list="currentBranch.images">
												<div slot="placeholder" class="image-slot">
													<div class="image-placeholder"></div>
												</div>
											</el-image>
											<div class="image-overlay"></div>
										</el-carousel-item>
									</el-carousel>
								</div>
							</div>
						</transition>
					</div>
				</section>

				<!-- Certificates Section -->
				<section class="section certificates-section">
					<div class="container">
						<div class="section-header center">
							<h2 class="section-title">{{ $t('aboutFst.certificates.title') }}</h2>
							<p class="section-subtitle">{{ $t('aboutFst.certificates.subtitle') }}</p>
						</div>

						<!-- 桌面轮播 -->
						<div class="carousel-container" v-if="!isMobileView">
							<el-carousel :interval="4000" type="card" height="350px">
								<el-carousel-item v-for="(cert, index) in certificates" :key="index">
									<el-image :src="cert.img" fit="contain" class="cert-img"
										:preview-src-list="certificates.map(c => c.img)" />
									<div class="cert-name">{{ cert.name }}</div>
								</el-carousel-item>
							</el-carousel>
						</div>

						<!-- 移动端平铺 -->
						<div class="cert-list" v-else>
							<div v-for="(cert, index) in certificates" :key="index" class="cert-item">
								<el-image :src="cert.img" fit="contain" class="cert-img"
									:preview-src-list="certificates.map(c => c.img)" />
								<div class="cert-name">{{ cert.name }}</div>
							</div>
						</div>

					</div>
				</section>

			</main>
		</div>
	</template>

	<script>
		export default {
			name: 'About',
			data() {
				return {
					isMuted: true,
					isPlaying: true,
					isMobileView: false,
					activeBranch: '北京总部',

					containerWidth: 1200,
					isMobile: false,
					isTablet: false
				}
			},
			computed: {
				branches() {
					return [{
							city: this.$t('aboutFst.branches.beijing.city'),
							year: this.$t('aboutFst.branches.beijing.year'),
							desc: this.$t('aboutFst.branches.beijing.desc'),
							officeArea: this.$t('aboutFst.branches.beijing.officeArea'),
							warehouseArea: this.$t('aboutFst.branches.beijing.warehouseArea'),
							images: [
								require('@/assets/images/北京办公室.jpg'),
								require('@/assets/images/北京办公室2.jpg'),
								require('@/assets/images/北京冷库 (1).jpg'),
								require('@/assets/images/北京冷库 (2).jpg'),
							]
						},
						{
							city: this.$t('aboutFst.branches.shanghai.city'),
							year: this.$t('aboutFst.branches.shanghai.year'),
							desc: this.$t('aboutFst.branches.shanghai.desc'),
							officeArea: this.$t('aboutFst.branches.shanghai.officeArea'),
							warehouseArea: this.$t('aboutFst.branches.shanghai.warehouseArea'),
							images: [
								require('@/assets/images/shky.jpg'),
								require('@/assets/images/shhy.jpg'),
								require('@/assets/images/上海变频冷库.jpg'),
							]
						},
						{
							city: this.$t('aboutFst.branches.guangzhou.city'),
							year: this.$t('aboutFst.branches.guangzhou.year'),
							desc: this.$t('aboutFst.branches.guangzhou.desc'),
							officeArea: this.$t('aboutFst.branches.guangzhou.officeArea'),
							warehouseArea: this.$t('aboutFst.branches.guangzhou.warehouseArea'),
							images: [
								require('@/assets/images/广州办公室.jpg'),
								require('@/assets/images/广州仓库 (2).jpg'),
								require('@/assets/images/广州冷库 (2).jpg'),
							]
						},
						{
							city: this.$t('aboutFst.branches.zhengzhou.city'),
							year: this.$t('aboutFst.branches.zhengzhou.year'),
							desc: this.$t('aboutFst.branches.zhengzhou.desc'),
							officeArea: this.$t('aboutFst.branches.zhengzhou.officeArea'),
							warehouseArea: this.$t('aboutFst.branches.zhengzhou.warehouseArea'),
							images: [
								require('@/assets/images/郑州办公室.jpg'),
							]
						},
						{
							city: this.$t('aboutFst.branches.chengdu.city'),
							year: this.$t('aboutFst.branches.chengdu.year'),
							desc: this.$t('aboutFst.branches.chengdu.desc'),
							officeArea: this.$t('aboutFst.branches.chengdu.officeArea'),
							warehouseArea: this.$t('aboutFst.branches.chengdu.warehouseArea'),
							images: [
								require('@/assets/images/成都办公室1.jpg'),
								require('@/assets/images/cdkf1.jpg'),
								require('@/assets/images/cdkf2.jpg'),
							]
						}
					]
				},
				certificates() {
					return [{
							img: require('@/assets/images/中国航空运输协会-会员证书.jpg'),
							name: this.$t('aboutFst.certificates.cert1')
						},
						{
							img: require('@/assets/images/质量管理体系认证证书.jpg'),
							name: this.$t('aboutFst.certificates.cert2')
						},
						{
							img: require('@/assets/images/WCA危险品专项会员2025.jpg'),
							name: this.$t('aboutFst.certificates.cert3')
						},
						{
							img: require('@/assets/images/WCA医药专项会员2025.jpg'),
							name: this.$t('aboutFst.certificates.cert4')
						},
						{
							img: require('@/assets/images/冷库温度显示装置检定报告.png'),
							name: this.$t('aboutFst.certificates.cert5')
						},
						{
							img: require('@/assets/images/CAST服务认证证书.jpg'),
							name: this.$t('aboutFst.certificates.cert6')
						}
					]
				},
				currentBranch() {
					return this.branches.find(b => b.city === this.activeBranch) || this.branches[0];
				},
				carouselHeight() {
					return this.isMobile ? '280px' : '350px';
				},
				certCarouselHeight() {
					return this.isMobile ? '280px' : (this.isTablet ? '320px' : '350px');
				}
			},
			mounted() {
				this.checkMobileView();
				window.addEventListener('resize', this.checkMobileView);
				console.log(this.$refs, "视频播放")
				this.checkViewport();
				window.addEventListener('resize', this.checkViewport);

				const savedBranch = localStorage.getItem('activeBranch');
				if (savedBranch && this.branches.some(b => b.city === savedBranch)) {
					this.activeBranch = savedBranch;
				}
				this.updateContainerWidth();
				window.addEventListener('resize', this.updateContainerWidth);
				this.tryAutoPlayWithSound();
				this.$nextTick(() => {
					const video = this.$refs.videoPlayer;
					if (video) {
						// 处理Safari等浏览器的自动播放限制
						video.play()
							.then(() => this.isPlaying = true)
							.catch(e => {
								console.log("Autoplay blocked:", e);
								this.isPlaying = false;
							});
					}
				});
			},
			beforeDestroy() {
				window.removeEventListener('resize', this.checkViewport);
				window.removeEventListener('resize', this.updateContainerWidth);
			},
			watch: {
				activeBranch(newVal) {
					localStorage.setItem('activeBranch', newVal);
				}
			},
			methods: {
				checkMobileView() {
					this.isMobileView = window.innerWidth <= 768;
				},
				togglePlay() {
					const video = this.$refs.videoPlayer;
					if (!video) return;

					if (this.isPlaying) {
						video.pause();
					} else {
						video.play().catch(e => {
							console.log("Playback failed:", e);
							video.muted = true;
							video.play();
						});
					}
					this.isPlaying = !this.isPlaying;
				},

				toggleMute() {
					const video = this.$refs.videoPlayer;
					if (!video) return;

					this.isMuted = !this.isMuted;
					video.muted = this.isMuted;
				},
				async tryAutoPlayWithSound() {
					const video = this.$refs.videoPlayer;
					if (!video) return;
					try {
						await video.play(); // 尝试播放
						// 默认静音，不需要取消静音
						video.muted = true;
						this.isMuted = true;
						this.isPlaying = true;
					} catch (err) {
						console.log("自动播放被阻止，需用户交互");
						video.muted = true;
						this.isMuted = true;
						this.isPlaying = false;
					}
				},
				toggleVideoSound() {
					const video = this.$refs.videoPlayer; // 改这里 ✅
					if (!video) return;

					video.muted = !video.muted;
					this.isMuted = video.muted;
				},
				checkViewport() {
					const width = window.innerWidth;
					this.isMobile = width <= 768;
					this.isTablet = width > 768 && width <= 1024;
				},
				updateContainerWidth() {
					if (this.$el) {
						this.containerWidth = this.$el.querySelector('.container').offsetWidth;
					}
				},
				getItemStyle(index) {
					const position = (index / (this.timelineData.length - 1)) * 100;
					return {
						left: `${position}%`,
						top: '50%',
						transform: index % 2 === 0 ? 'translate(-50%, -120%)' : 'translate(-50%, 20px)'
					};
				},
				getAdvantageIcon(index) {
					const icons = ['Connection', 'User', 'DataBoard', 'House'];
					return icons[index] || 'Star';
				}
			},
			beforeDestroy() {
				window.removeEventListener('resize', this.checkMobileView);
			},
		}
	</script>

	<style lang="scss" scoped>
		.about-page {
			display: flex;
			flex-direction: column;
			min-height: 100vh;
			overflow-x: hidden;
			font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
			color: #333;
			line-height: 1.6;
			background: linear-gradient(rgba(245, 250, 255, 0.92), rgba(245, 250, 255, 0.92)),
				url('../assets/images/背景1.jpg') center/cover no-repeat;

			.hero-banner {
				width: 100%;
				height: 60vh;
				min-height: 400px;
				position: relative;
				background: linear-gradient(105deg, rgba(16, 70, 212, 0.15) 0%, rgba(100, 180, 255, 0.25) 50%, rgba(225, 245, 255, 0.4) 100%),
					url('../assets/images/zjwm.png') center/cover no-repeat;
				display: flex;
				align-items: center;
				justify-content: center;
				box-shadow: 0 4px 20px rgba(0, 60, 120, 0.1);

				.hero-container {
					max-width: 1200px;
					width: 90%;
					margin: 0 auto;
					padding: 40px;
					background-color: rgba(255, 255, 255, 0.2);
					backdrop-filter: blur(8px);
					border-radius: 12px;
					border: 1px solid rgba(255, 255, 255, 0.3);
					box-shadow: 0 8px 32px rgba(0, 60, 120, 0.1);
				}

				.hero-content {
					max-width: 800px;
					text-align: center;
					animation: fadeInUp 1.2s ease-out;

					h1 {
						font-size: clamp(2.5rem, 6vw, 3.5rem);
						font-weight: 700;
						margin-bottom: 1.5rem;
						color: #fff;
						text-shadow: 0 2px 12px rgba(0, 60, 120, 0.6);
						letter-spacing: 0.5px;
						line-height: 1.2;
						position: relative;
						display: inline-block;

						&::after {
							content: '';
							display: block;
							width: 80px;
							height: 4px;
							background: rgba(255, 255, 255, 0.7);
							margin: 20px auto;
							border-radius: 2px;
						}
					}

					.subtitle {
						font-size: clamp(1.2rem, 3vw, 1.5rem);
						font-weight: 500;
						color: #fff;
						margin-bottom: 2rem;
						letter-spacing: 1.5px;
						line-height: 1.4;
						position: relative;
						display: inline-block;
						padding: clamp(0.8rem, 2vw, 1.2rem) clamp(1.5rem, 4vw, 2.5rem);
						background: linear-gradient(to right, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%);
						border: 1px solid rgba(255, 255, 255, 0.35);
						border-radius: 50px;
						backdrop-filter: blur(6px);
						box-shadow: 0 4px 15px rgba(0, 60, 120, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.2);

						&::after {
							content: '';
							display: block;
							width: 40%;
							height: 2px;
							background: linear-gradient(to right, rgba(255, 255, 255, 0.8), transparent);
							margin: 14px auto 0;
							opacity: 0.7;
						}
					}
				}
			}

			.main-content {
				flex: 1;
				width: 100%;
				max-width: 1440px;
				margin: 0 auto;

				.section {
					margin-bottom: 40px;

					&:last-child {
						margin-bottom: 0;
					}

					.container {
						width: 100%;
					}
				}

				.section-header {
					margin-bottom: 80px;

					&.center {
						text-align: center;
					}

					.section-title {
						font-size: 42px;
						font-weight: 600;
						margin-bottom: 15px;
						color: #1a3e72;
						position: relative;
						display: inline-block;

						&::after {
							content: '';
							position: absolute;
							bottom: -15px;
							left: 0;
							width: 80px;
							height: 4px;
							background: linear-gradient(to right, #1a6fdf, #4facfe);
							border-radius: 2px;
						}
					}

					.section-subtitle {
						font-size: 18px;
						color: #6c757d;
						font-weight: 300;
						letter-spacing: 1px;
					}
				}

				/* 公司简介网格布局 */
				.company-intro-section {
					padding: 80px 0;
					position: relative;
				}

				.container {
					max-width: 1200px;
					margin: 0 auto;
					padding: 0 20px;
				}

				.section-header {
					text-align: center;
					margin-bottom: 50px;
				}

				.section-title {
					font-size: 2.2rem;
					color: #2c3e50;
					display: flex;
					align-items: center;
					justify-content: center;
					margin-bottom: 10px;
				}

				.section-title .el-icon {
					font-size: 1.8rem;
					margin-right: 15px;
					color: #409EFF;
				}

				.section-subtitle {
					font-size: 1.1rem;
					color: #7f8c8d;
					margin-bottom: 20px;
				}

				.title-decoration {
					width: 60px;
					height: 3px;
					background: linear-gradient(to right, #409EFF, #67C23A);
					margin: 0 auto;
					border-radius: 3px;
				}

				.intro-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 40px;
					margin-bottom: 50px;
				}

				.intro-content {
					display: flex;
					flex-direction: column;
					gap: 30px;
				}

				.intro-card {
					background: white;
					border-radius: 10px;
					padding: 25px;
					box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03);
					transition: transform 0.3s ease, box-shadow 0.3s ease;
				}

				.intro-card:hover {
					transform: translateY(-5px);
					box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
				}

				.card-header {
					display: flex;
					align-items: center;
					margin-bottom: 15px;
				}

				.card-header .el-icon {
					font-size: 1.5rem;
					color: #409EFF;
					margin-right: 15px;
				}

				.card-header h3 {
					font-size: 1.3rem;
					color: #2c3e50;
					margin: 0;
				}

				.card-text {
					color: #555;
					line-height: 1.8;
					margin: 0 0 15px 0;
				}

				.highlight {
					font-weight: bold;
				}

				.service-tags {
					display: flex;
					flex-wrap: wrap;
					gap: 16px;

					.service-tag {
						font-size: 16px;
						color: #333;
						font-weight: 400;
						padding: 4px 0;
						position: relative;
					}

					.service-tag::before {
						content: "•";
						color: #409EFF;
						margin-right: 8px;
						font-size: 18px;
						vertical-align: middle;
					}
				}

				::v-deep(.solid-color-tag) {
					background-color: #409EFF !important;
					border: none;
					color: #fff !important;
					font-weight: bold;
					padding: 6px 14px;
					font-size: 14px;
					cursor: default;
				}

				.advantage-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 20px;
				}

				.advantage-item {
					display: flex;
					align-items: flex-start;
					gap: 15px;
				}

				.advantage-item .el-icon {
					font-size: 1.8rem;
					color: #409EFF;
					margin-top: 3px;
				}

				.advantage-title {
					font-weight: 600;
					color: #2c3e50;
					margin-bottom: 5px;
				}

				.advantage-item p {
					color: #666;
					margin: 0;
					font-size: 0.95rem;
				}

				.intro-visual {
					display: flex;
					flex-direction: column;
					gap: 30px;
				}

				.company-image {
					border-radius: 10px;
					overflow: hidden;
					box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
					position: relative;
				}

				.center-play-button {
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%);
					color: white;
					width: 60px;
					height: 60px;
					border-radius: 50%;
					display: flex;
					align-items: center;
					justify-content: center;
					cursor: pointer;
					z-index: 10;
					transition: all 0.3s;
				}

				.center-play-button i {
					font-size: 30px;
					margin-left: 5px;
					/* 播放图标向右微调 */
				}

				.center-play-button:hover {
					transform: translate(-50%, -50%) scale(1.1);
				}

				/* 静音按钮（右下角） */
				.mute-button {
					position: absolute;
					bottom: 15px;
					right: 15px;
					background: rgba(0, 0, 0, 0.6);
					color: white;
					width: 32px;
					height: 32px;
					border-radius: 50%;
					display: flex;
					align-items: center;
					justify-content: center;
					cursor: pointer;
					z-index: 10;
				}

				.mute-button:hover {
					background: rgba(0, 0, 0, 0.8);
				}

				.intro-img {
					width: 100%;
					height: 350px;
					display: block;
					transition: transform 0.5s ease;
				}

				.intro-img:hover {
					transform: scale(1.03);
				}

				.image-caption {
					position: absolute;
					bottom: 0;
					left: 0;
					right: 0;
					background: rgba(0, 0, 0, 0.6);
					color: white;
					padding: 10px 20px;
					text-align: center;
					font-size: 0.9rem;
				}

				.image-loading,
				.image-error {
					height: 350px;
					display: flex;
					align-items: center;
					justify-content: center;
					background: #f5f7fa;
					color: #999;
				}

				.image-loading .el-icon-loading {
					font-size: 2rem;
				}

				.image-error .el-icon-picture-outline {
					font-size: 2rem;
				}

				.stats-container {
					background: linear-gradient(135deg, #409EFF 0%, #67C23A 100%);
					border-radius: 10px;
					padding: 30px;
					color: white;
				}

				.stats-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 30px;
				}

				.stat-item {
					text-align: center;
				}

				.stat-icon .el-icon {
					font-size: 2rem;
					margin-bottom: 10px;
					color: white;
				}

				.stat-number {
					font-size: 2rem;
					font-weight: 700;
					margin-bottom: 5px;
				}

				.stat-label {
					font-size: 0.95rem;
					opacity: 0.9;
				}

				.mission-statement {
					background: linear-gradient(135deg, #f5fbfe 0%, #e8f5e9 100%);
					border-radius: 10px;
					padding: 30px;
					display: flex;
					align-items: center;
					gap: 20px;
				}

				.mission-statement .el-icon {
					font-size: 2.5rem;
					color: #E6A23C;
					flex-shrink: 0;
				}

				.mission-content h3 {
					color: #2c3e50;
					margin: 0 0 10px 0;
					font-size: 1.5rem;
				}

				.mission-content p {
					color: #555;
					line-height: 1.8;
					margin: 0;
				}

				/* 响应式设计 */
				@media (max-width: 992px) {
					.intro-grid {
						grid-template-columns: 1fr;
					}

					.intro-img {
						height: 300px;
					}
				}

				@media (max-width: 768px) {

					.advantage-grid,
					.stats-grid {
						grid-template-columns: 1fr;
					}

					.mission-statement {
						flex-direction: column;
						text-align: center;
					}

					.section-title {
						font-size: 1.8rem;
					}
				}

				@media (max-width: 576px) {
					.company-intro-section {
						padding: 60px 0;
					}

					.service-tags {
						justify-content: center;
					}

					.intro-card {
						padding: 20px;
					}
				}

				.development-section {
					padding: 80px 0;
					position: relative;
					overflow: hidden;

					&::before {
						content: '';
						position: absolute;
						bottom: 0;
						left: 0;
						width: 100%;
						height: 50%;
						background: url('~@/assets/images/lc-quxian.png') no-repeat center bottom;
						background-size: contain;
						z-index: 1;
						opacity: 0.8;
					}
				}

				.timeline-container {
					position: relative;
					height: 500px;
				}

				.timeline-track {
					position: relative;
					width: 100%;
					height: 100%;
				}

				.timeline-item {
					position: absolute;
					transform: translate(-50%, -50%);
					width: 200px;
					text-align: center;
					z-index: 2;

					.timeline-dot {
						width: 18px;
						height: 18px;
						background-color: #0a3875;
						border-radius: 50%;
						margin: 0 auto;
						position: relative;
					}

					.timeline-year {
						font-size: 20px;
						font-weight: bold;
						color: #0a3875;
						margin: 10px 0;
					}

					.timeline-events {
						background: white;
						padding: 15px;
						border-radius: 8px;
						box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);

						.event-item {
							font-size: 16px;
							line-height: 1.6;
							color: #333;

							&:not(:last-child) {
								margin-bottom: 8px;
							}
						}
					}

					&.even {
						.timeline-dot::after {
							content: '';
							position: absolute;
							top: 100%;
							left: 50%;
							transform: translateX(-50%);
							width: 1px;
							height: 30px;
							background-color: #0a3875;
						}
					}

					&:not(.even) {
						.timeline-dot::before {
							content: '';
							position: absolute;
							bottom: 100%;
							left: 50%;
							transform: translateX(-50%);
							width: 1px;
							height: 30px;
							background-color: #0a3875;
						}
					}
				}

				/* Responsive */
				@media (max-width: 768px) {
					.timeline-container {
						height: auto;
					}

					.timeline-item {
						position: static;
						transform: none;
						width: 100%;
						margin-bottom: 30px;
						display: flex;
						align-items: flex-start;
						gap: 12px;
						text-align: left;

						.timeline-dot {
							flex-shrink: 0;
						}

						.timeline-year {
							font-size: 18px;
							margin: 0;
						}

						.timeline-events {
							flex: 1;
							margin-top: auto !important;
						}

						.timeline-dot::before,
						.timeline-dot::after {
							display: none;
						}
					}

					.section-header {
						margin-bottom: 40px;

						.section-title {
							font-size: 28px;
						}

						.section-subtitle {
							font-size: 16px;
						}
					}
				}

				:root {
					--primary-color: #007BFF;
					--primary-rgb: 0, 123, 255;
					--text-light: #888;
					--transition: all 0.3s ease;
				}

				/* 分公司样式 */
				.branches-section {
					background: var(--white);
					padding: 10vh 0;
					position: relative;

					&::before {
						content: '';
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						opacity: 0.03;
						pointer-events: none;
					}
				}

				.branch-selector {
					display: flex;
					justify-content: center;
					flex-wrap: wrap;
					gap: 12px;
					margin-bottom: 4rem;
				}

				.branch-tab {
					position: relative;
					cursor: pointer;
					transition: var(--transition);
					padding: 0.8rem 1.6rem;
					margin: 0;
					border-radius: 10px;
					background-color: #fff;
					border: 2px solid transparent;
					box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
					user-select: none;

					&.active {
						background: linear-gradient(135deg, var(--primary-color), #0056d2);
						color: #1a3e72;
						border-color: transparent;
						box-shadow: 0 6px 16px rgba(var(--primary-rgb), 0.35);
						transform: translateY(-3px) scale(1.03);

						.tab-content {
							transform: translateY(-2px);
							font-weight: 600;

							.city {
								font-size: 1.2rem;
								letter-spacing: 0.3px;
							}
						}

						.tab-indicator {
							width: 60%;
							height: 4px;
							background: #fff;
							opacity: 1;
							border-radius: 4px;
							bottom: 6px;
							left: 50%;
							transform: translateX(-50%);
						}
					}
				}

				.tab-content {
					transition: var(--transition);
					text-align: center;

					.city {
						font-size: 1.1rem;
						font-weight: bold;
					}

					.year {
						font-size: 0.85rem;
						opacity: 0.8;
					}
				}

				.tab-indicator {
					position: absolute;
					bottom: 0;
					left: 0;
					width: 0;
					height: 3px;
					background: var(--primary-color);
					border-radius: 2px;
					transition: var(--transition);
					opacity: 0;
				}

				/* 分公司详情过渡动画 */
				.fade-slide-enter-active,
				.fade-slide-leave-active {
					transition: all 0.5s ease;
				}

				.fade-slide-enter,
				.fade-slide-leave-to {
					opacity: 0;
					transform: translateY(20px);
				}

				.branch-detail {
					width: 80vw;
					margin: auto;
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 4vw;
					background: var(--white);
					border-radius: 16px;
					overflow: hidden;
					box-shadow: var(--shadow-md);
				}

				.branch-info {
					padding: 3rem;

					h3 {
						font-size: clamp(1.5rem, 3vw, 2rem);
						color: var(--dark-color);
						margin-bottom: 1.5rem;
						position: relative;
						padding-bottom: 1rem;

						&::after {
							content: '';
							position: absolute;
							bottom: 0;
							left: 0;
							width: 60px;
							height: 4px;
							background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
							border-radius: 2px;
						}
					}
				}

				.branch-desc {
					p {
						font-size: clamp(0.95rem, 1.8vw, 1.05rem);
						line-height: 1.8;
						color: var(--text-color);
						margin-bottom: 2.5rem;
					}
				}

				.branch-stats {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 20px;

					.stat-item {
						display: flex;
						align-items: center;
						background: var(--light-color);
						border-radius: 10px;
						padding: 1.5rem;
						transition: var(--transition);

						.stat-icon {
							width: 50px;
							height: 50px;
							border-radius: 50%;
							background: var(--white);
							display: flex;
							align-items: center;
							justify-content: center;
							margin-right: 1rem;
							color: var(--primary-color);
							font-size: 1.5rem;
							transition: var(--transition);
							flex-shrink: 0;
						}

						.stat-text {
							overflow-wrap: anywhere;
							word-break: break-word;

							.stat-title {
								font-size: clamp(0.6rem, 1.3vw, 0.7rem);
								color: var(--text-light);
								margin-bottom: 0.3rem;
								display: -webkit-box;
								-webkit-box-orient: vertical;
								-webkit-line-clamp: 2;
								overflow: hidden;
							}

							.stat-value {
								font-size: clamp(1.1rem, 2.2vw, 1.3rem);
								font-weight: 600;
								color: var(--dark-color);
								overflow-wrap: anywhere;
								word-break: break-word;
							}
						}
					}
				}

				@media (max-width: 768px) {
					.branch-stats {
						grid-template-columns: 1fr;
					}
				}

				.branch-gallery {
					position: relative;
					height: 100%;
					min-height: 400px;

					.el-carousel {
						height: 100%;
					}

					.branch-image {
						width: 100%;
						height: 100%;
						object-fit: cover;
					}

					.image-overlay {
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						background: linear-gradient(to top, rgba(0, 0, 0, 0.5), transparent 50%);
					}
				}

				@media (max-width: 768px) {
					.branch-selector {
						gap: 8px;
						margin-bottom: 2rem;
					}

					.branch-tab {
						padding: 0.5rem 1rem;

						&.active {
							transform: translateY(-2px) scale(1.02);
						}
					}

					.tab-content {
						.city {
							font-size: 1rem;
						}

						.year {
							font-size: 0.75rem;
						}
					}

					.branch-detail {
						grid-template-columns: 1fr; // 改为单列纵向排布
						width: 95vw;
						gap: 2rem;
					}

					.branch-info {
						padding: 2rem 1.5rem;

						h3 {
							font-size: 1.5rem;
							padding-bottom: 0.8rem;

							&::after {
								width: 40px;
								height: 3px;
							}
						}
					}

					.branch-desc {
						p {
							font-size: 0.95rem;
							line-height: 1.6;
							margin-bottom: 1.5rem;
						}
					}

					.branch-stats {
						grid-template-columns: 1fr; // 改为一列竖排
						gap: 1rem;

						.stat-item {
							padding: 1rem;

							.stat-icon {
								width: 40px;
								height: 40px;
								font-size: 1.2rem;
								margin-right: 0.8rem;
							}

							.stat-text {
								.stat-title {
									font-size: 0.8rem;
								}

								.stat-value {
									font-size: 1.1rem;
								}
							}
						}
					}

					.branch-gallery {
						min-height: 250px;

						.el-carousel {
							height: 250px;
						}

						.branch-image {
							height: 250px;
						}

						.image-overlay {
							background: linear-gradient(to top, rgba(0, 0, 0, 0.4), transparent 50%);
						}
					}
				}

				/* 证书轮播布局 */
				.certificates-section {
					position: relative;
					overflow: hidden;

					&::before {
						content: '';
						position: absolute;
						top: -100px;
						right: -100px;
						width: 300px;
						height: 300px;
						// background: radial-gradient(circle, rgba(26, 111, 223, 0.1) 0%, rgba(26, 111, 223, 0) 70%);
						z-index: 1;
					}

					.carousel-container {
						position: relative;
						z-index: 2;
						padding: 0 40px;
						padding-bottom: 80px;

						::v-deep .el-carousel__container {
							height: 450px !important;
						}

						::v-deep .el-carousel--horizontal {
							overflow-x: clip;
						}

						::v-deep .el-carousel__item {
							background-color: #fff;
							border-radius: 16px;
							display: flex;
							flex-direction: column;
							align-items: center;
							justify-content: center;
							padding: 40px;
							box-shadow: 0 15px 30px rgba(26, 111, 223, 0.1);
							border: 1px solid rgba(26, 111, 223, 0.1);
							transition: all 0.4s ease;
							overflow: hidden;
							margin: 0 15px;

							&:hover {
								transform: translateY(-5px);
								box-shadow: 0 20px 40px rgba(26, 111, 223, 0.2);
							}
						}

						::v-deep .el-carousel__mask {
							background-color: transparent;
						}

						.cert-img {
							width: 100%;
							height: 350px;
							cursor: pointer;
							object-fit: contain;
							transition: transform 0.5s ease;
							padding: 20px;

							&:hover {
								transform: scale(1.05);
							}
						}

						.cert-name {
							margin-top: 25px;
							font-size: 18px;
							font-weight: 600;
							color: #1a3e72;
							text-align: center;
							position: relative;
							padding-bottom: 15px;

							&::after {
								content: '';
								position: absolute;
								bottom: 0;
								left: 50%;
								transform: translateX(-50%);
								width: 40px;
								height: 2px;
								background: linear-gradient(to right, #1a6fdf, #4facfe);
							}
						}
					}
				}
			}

			.image-slot {
				display: flex;
				justify-content: center;
				align-items: center;
				width: 100%;
				height: 100%;
				background: #f8fafd;
				color: #adb5bd;
				font-size: 16px;
				font-weight: 300;
				border-radius: 12px;
			}

			@keyframes fadeInUp {
				from {
					opacity: 0;
					transform: translateY(30px);
				}

				to {
					opacity: 1;
					transform: translateY(0);
				}
			}

			/* 响应式调整 */
			@media (max-width: 1440px) {

				.hero-banner .hero-container,
				.main-content {
					max-width: 1200px;
				}
			}

			@media (max-width: 1200px) {
				.hero-banner {
					height: 60vh;
					min-height: 500px;
				}

				.main-content {
					padding: 60px 5%;

					.section {
						margin-bottom: 80px;
					}
				}
			}

			@media (max-width: 992px) {
				.hero-banner {
					height: 55vh;
					min-height: 450px;
				}

				.main-content {
					padding: 50px 5%;

					.section {
						margin-bottom: 70px;
					}

					.section-header {
						margin-bottom: 50px;
					}
				}
			}

			@media (max-width: 768px) {
				.hero-banner {
					height: 50vh;
					min-height: 400px;

					.hero-container {
						padding: 0 20px;
					}

					.hero-content {
						h1 {
							font-size: 42px;
						}

						.subtitle {
							font-size: 18px;
						}
					}
				}

				.main-content {
					padding: 40px 20px;

					.section {
						margin-bottom: 60px;
					}

					.section-header {
						margin-bottom: 40px;

						.section-title {
							font-size: 36px;
						}
					}

					.carousel-container {
						padding: 0;
					}
				}
			}

			@media (max-width: 576px) {
				.hero-banner {
					height: 45vh;
					min-height: 350px;

					.hero-content {
						h1 {
							font-size: 32px;
						}

						.subtitle {
							font-size: 16px;
						}
					}
				}

				.main-content {
					padding: 30px 15px;
					padding-left: 0px;

					.section {
						margin-bottom: 50px;
					}

					.section-header {
						margin-bottom: 30px;

						.section-title {
							font-size: 28px;

							&::after {
								width: 60px;
								height: 3px;
								bottom: -10px;
							}
						}
					}

					.stats-grid {
						grid-template-columns: 1fr !important;
						gap: 20px;
					}
				}
			}

			/* 新增的响应式增强样式 */
			@media (max-width: 1024px) {
				.intro-grid {
					gap: 30px;
				}

				.branch-detail {
					grid-template-columns: 1fr;
					width: 90vw;
				}

				.branch-gallery {
					order: -1;
					min-height: 300px;
				}

				.certificates-section ::v-deep .el-carousel__container {
					height: 320px !important;
				}
			}

			@media (max-width: 480px) {
				.hero-banner {
					min-height: 60vh;

					.hero-content h1 {
						font-size: 1.8rem;
					}
				}

				.section-header .section-title {
					font-size: 1.8rem;
				}

				.branch-selector {
					flex-wrap: nowrap;
					overflow-x: auto;
					padding-bottom: 10px;
					justify-content: flex-start;
				}

				.branch-tab {
					flex: 0 0 auto;
					padding: 0.6rem 1.2rem;
				}

				.certificates-section ::v-deep .el-carousel--card .el-carousel__item {
					width: 85% !important;
					margin: 0 7.5% !important;
				}

				.certificates-section ::v-deep .el-carousel__container {
					height: 280px !important;
				}
			}

			/* 高DPI设备优化 */
			@media only screen and (-webkit-min-device-pixel-ratio: 1.5),
			only screen and (min-resolution: 144dpi) {
				.hero-banner {
					// background-image: linear-gradient(105deg, rgba(16, 70, 212, 0.15) 0%, rgba(100, 180, 255, 0.25) 50%, rgba(225, 245, 255, 0.4) 100%),
					// 	url('../assets/images/zjwm@2x.png');
					// background-size: cover;
				}
			}

			/* 打印样式 */
			@media print {

				.hero-banner,
				.branch-selector {
					display: none;
				}

				.main-content {
					padding: 0 !important;
				}

				.section {
					page-break-inside: avoid;
				}
			}
		}

		.mute-button {
			position: absolute;
			top: 10px;
			right: 10px;
			background: rgba(0, 0, 0, 0.5);
			color: white;
			width: 32px;
			height: 32px;
			border-radius: 50%;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			z-index: 10;
		}

		.mute-button:hover {
			background: rgba(0, 0, 0, 0.7);
		}

		.company-image {
			position: relative;
			/* 让静音按钮能绝对定位 */
		}

		svg {
			fill: #333;
			/* 可以根据需要自定义颜色 */
		}

		@media screen and (max-width: 768px) {

			/* 容器左右内边距，保证不贴边 */
			.container {
				padding-left: 16px !important;
				padding-right: 16px !important;
			}

			/* intro-grid竖直堆叠，居中 */
			.intro-grid {
				flex-direction: column !important;
				align-items: center !important;
			}

			.intro-content,
			.intro-visual {
				width: 100% !important;
				padding-right: 0 !important;
			}

			/* 分支详情改纵向，居中 */
			.branch-detail {
				flex-direction: column !important;
				align-items: center !important;
			}

			.branch-info,
			.branch-gallery {
				width: 100% !important;
				padding-right: 0 !important;
			}

			.branch-stats .stat-item {
				flex: 1 1 100% !important;
				text-align: center !important;
			}

			/* 分支tab换为两列，居中 */
			.branch-selector {
				justify-content: center !important;
				flex-wrap: wrap !important;
			}

			.branch-tab {
				flex-basis: 45% !important;
				margin: 4px 0 !important;
			}

			// /* 时间线改为横向滚动 */
			// .timeline-container {
			// 	overflow-x: auto !important;
			// 	white-space: nowrap !important;
			// 	padding-bottom: 10px !important;
			// }

			// .timeline-track {
			// 	display: inline-flex !important;
			// 	flex-direction: row !important;
			// 	position: relative !important;
			// 	align-items: flex-start !important;
			// }

			// .timeline-item {
			// 	position: relative !important;
			// 	left: unset !important;
			// 	top: unset !important;
			// 	margin: 0 12px !important;
			// 	display: inline-block !important;
			// }

			/* 证书移动端平铺 */
			.cert-list {
				display: flex !important;
				flex-direction: column !important;
				align-items: center !important;
			}

			.cert-item {
				width: 80% !important;
			}

			/* 视频容器自适应 */
			.company-image video.intro-img {
				width: 100% !important;
				height: auto !important;
				display: block !important;
				margin: 0 auto !important;
			}
		}

		::v-deep p {
			word-break: break-word;
			hyphens: auto;
		}
	</style>